import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { TrendingUp, Brain, Users, DollarSign, Target, BarChart3, Ticket, Award, RefreshCw, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { useCurrency } from '../contexts/CurrencyContext';
const API = process.env.REACT_APP_BACKEND_URL;

const MetricCard = ({ label, value, icon: Icon, color, prefix, suffix, trend }) => (
  <Card className="border-gray-200 hover:shadow-sm transition-shadow" data-testid={`bi-metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between mb-2">
        <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="h-4 w-4" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-0.5 text-xs font-medium ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-500' : 'text-gray-400'}`}>
            {trend > 0 ? <ArrowUpRight className="h-3 w-3" /> : trend < 0 ? <ArrowDownRight className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}</p>
      <p className="text-xs text-gray-400 mt-0.5">{label}</p>
    </CardContent>
  </Card>
);

export default function BusinessIntelligencePage() {
  const { format: fc, symbol: currSymbol } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const { data: d } = await axios.get(`${API}/api/platform/intelligence`);
      setData(d);
    } catch {
      toast.error('Failed to load intelligence data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="flex justify-center py-20" data-testid="bi-loading">
      <div className="w-8 h-8 rounded-full border-2 border-cyan-500 border-t-transparent animate-spin" />
    </div>
  );

  const metrics = data ? [
    { label: 'Total Leads', value: data.total_leads, icon: Users, color: 'text-blue-500 bg-blue-50', trend: 12 },
    { label: 'Total Deals', value: data.total_deals, icon: Target, color: 'text-purple-500 bg-purple-50', trend: 8 },
    { label: 'Deals Won', value: data.deals_won, icon: Award, color: 'text-green-500 bg-green-50', trend: 15 },
    { label: 'Conversion Rate', value: data.conversion_rate, icon: TrendingUp, color: 'text-cyan-500 bg-cyan-50', suffix: '%', trend: data.conversion_rate > 0 ? 5 : 0 },
    { label: 'Pipeline Value', value: data.pipeline_value, icon: DollarSign, color: 'text-amber-500 bg-amber-50', prefix: currSymbol, trend: 10 },
    { label: 'Won Revenue', value: data.won_value, icon: BarChart3, color: 'text-emerald-500 bg-emerald-50', prefix: currSymbol, trend: 18 },
    { label: 'Loyalty Members', value: data.loyalty_members, icon: Award, color: 'text-pink-500 bg-pink-50', trend: 6 },
    { label: 'Open Tickets', value: data.open_tickets, icon: Ticket, color: 'text-red-500 bg-red-50', trend: data.open_tickets > 0 ? -3 : 0 },
  ] : [];

  const insights = data ? [
    { title: 'Sales Conversion', desc: data.conversion_rate > 0 ? `${data.conversion_rate}% of deals are converting to wins. ${data.conversion_rate > 20 ? 'Great performance!' : 'Consider optimizing your sales process.'}` : 'No deal data yet. Start adding deals to track conversion.', impact: data.conversion_rate > 20 ? 'positive' : data.conversion_rate > 0 ? 'warning' : 'info' },
    { title: 'Pipeline Health', desc: data.pipeline_value > 0 ? `${fc(data.pipeline_value)} in active pipeline across ${data.total_deals} deals.` : 'Pipeline is empty. Focus on lead generation.', impact: data.pipeline_value > 0 ? 'positive' : 'warning' },
    { title: 'Customer Support', desc: data.open_tickets > 0 ? `${data.open_tickets} open tickets need attention. Resolution rate: ${data.resolution_rate}%` : 'No open tickets. Support queue is clear!', impact: data.open_tickets === 0 ? 'positive' : data.open_tickets > 10 ? 'warning' : 'info' },
    { title: 'Loyalty Program', desc: data.loyalty_members > 0 ? `${data.loyalty_members} active loyalty members driving repeat business.` : 'No loyalty members yet. Consider launching a loyalty program.', impact: data.loyalty_members > 0 ? 'positive' : 'info' },
  ] : [];

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="business-intelligence-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-cyan-50 flex items-center justify-center">
            <Brain className="h-5 w-5 text-cyan-600" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Business Intelligence<PageInfoTooltip pageKey="business-intelligence" /></h1>
            <p className="text-xs text-gray-400">Real-time insights from your data</p>
          </div>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="gap-1.5 self-start"
          data-testid="bi-refresh-btn"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {!data ? (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="p-10 text-center">
            <Brain className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">No data available</h3>
            <p className="text-sm text-gray-400">Start adding leads, deals, and customers to see insights</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {metrics.map((m, i) => <MetricCard key={i} {...m} />)}
          </div>

          <Card className="border-gray-200">
            <CardContent className="p-4">
              <h2 className="text-sm font-heading font-bold text-gray-700 mb-3">AI Insights</h2>
              <div className="space-y-2">
                {insights.map((ins, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-3 p-3 rounded-lg ${ins.impact === 'positive' ? 'bg-green-50' : ins.impact === 'warning' ? 'bg-amber-50' : 'bg-blue-50'}`}
                    data-testid={`bi-insight-${i}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${ins.impact === 'positive' ? 'bg-green-200 text-green-700' : ins.impact === 'warning' ? 'bg-amber-200 text-amber-700' : 'bg-blue-200 text-blue-700'}`}>
                      <TrendingUp className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">{ins.title}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{ins.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card className="border-gray-200" data-testid="bi-summary-pipeline">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-400 mb-1">Pipeline → Won</p>
                <p className="text-lg font-bold text-gray-900">${data.pipeline_value.toLocaleString()}</p>
                <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
                  <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${Math.min((data.won_value / (data.pipeline_value || 1)) * 100, 100)}%` }} />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">${data.won_value.toLocaleString()} won</p>
              </CardContent>
            </Card>
            <Card className="border-gray-200" data-testid="bi-summary-resolution">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-400 mb-1">Ticket Resolution</p>
                <p className="text-lg font-bold text-gray-900">{data.resolution_rate}%</p>
                <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
                  <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${data.resolution_rate}%` }} />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">{data.open_tickets} open</p>
              </CardContent>
            </Card>
            <Card className="border-gray-200" data-testid="bi-summary-conversion">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-400 mb-1">Deal Conversion</p>
                <p className="text-lg font-bold text-gray-900">{data.conversion_rate}%</p>
                <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
                  <div className="bg-purple-500 h-2 rounded-full transition-all" style={{ width: `${Math.min(data.conversion_rate, 100)}%` }} />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">{data.deals_won} of {data.total_deals} deals</p>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
